/**
 * R-TRK-4 — validating what a member writes about a trunk (design §22.5.1, AC-TRK-4).
 *
 * The input is the public body as it arrives (`SipTrunkCreate`, snake_case, `unknown`). Every error
 * names its field (`host`, `allowed_address[2]`, `number[0].e164`) and carries the FIRST failing rule
 * for that field; every field is checked, so one answer lists everything wrong. Nothing
 * half-validates: the result is either the whole typed value or the rejection, never both.
 *
 * The password is checked for length only and never appears in an error — neither its value nor
 * its length. Unknown fields are refused by name, so a misspelt `passwrod` is not silently dropped.
 */
import { isReviewedDestinationCountry } from "./destination-prefix.js";
import { isSipDialHost, isSipWireForm } from "./dial-shape.js";
import { nationalNumberRuleOf } from "./national-number-rule.js";
import { CALLER_ID_PRESENTATION, SIP_CODEC_POLICY, SIP_INBOUND_DELIVERY, SIP_MEDIA_ENCRYPTION, SIP_TRANSPORT, SIP_TRUNK_LIMIT, SIP_TRUNK_OUTBOUND_FIELD, WIRE_NUMBER_FORMAT, isCountryCode, } from "./sip-trunk.js";
import { isE164, toWireNumber } from "./wire-number.js";
export function isSipTrunkInputRejection(value) {
    return typeof value === "object" && value !== null && value.kind === "rejected";
}
const ok = (value) => ({ ok: true, value });
const fail = (code) => ({ ok: false, code });
/** Code points, so an accented display name is counted as a person reads it. */
function lengthOf(value) {
    return [...value].length;
}
// eslint-disable-next-line no-control-regex -- the point is to find control characters
const CONTROL_CHARACTER = /[\u0000-\u001f\u007f]/;
function checkDisplayName(raw) {
    if (raw === undefined)
        return fail("required");
    if (typeof raw !== "string")
        return fail("wrong_type");
    if (raw.trim().length === 0)
        return fail("empty");
    if (lengthOf(raw) > SIP_TRUNK_LIMIT.displayNameMaxLength)
        return fail("too_long");
    if (CONTROL_CHARACTER.test(raw))
        return fail("not_printable");
    return ok(raw);
}
function checkOption(raw, option) {
    if (raw === undefined)
        return fail("required");
    if (typeof raw !== "string")
        return fail("wrong_type");
    return option.includes(raw) ? ok(raw) : fail("not_an_option");
}
function checkPort(raw) {
    if (raw === undefined)
        return fail("required");
    if (typeof raw !== "number" || !Number.isInteger(raw))
        return fail("wrong_type");
    return raw >= 1 && raw <= 65535 ? ok(raw) : fail("out_of_range");
}
const LDH_LABEL = /^[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?$/;
const NUMERIC_LABEL = /^[0-9]+$/;
/**
 * A DNS name (LDH labels, each ≤ 63, ≤ 253 in all, no trailing dot) or an IPv4 literal. No
 * scheme, no `user@`, no `:port`, no `;parameter`, no brackets — each of those fails the shapes
 * below. An IP literal must be public: the SIP service dials it from the VM. An IPv6 literal is
 * refused as `host_ipv6_unsupported` (integration ML1, 2026-09-30): the SIP edge's dial does not carry
 * one yet (`SIP_DIAL_HOST_PATTERN`), so accepting it would fail every dial after a 201.
 */
function checkHost(raw) {
    if (raw === undefined)
        return fail("required");
    if (typeof raw !== "string")
        return fail("wrong_type");
    if (raw.length === 0)
        return fail("empty");
    if (raw.length > SIP_TRUNK_LIMIT.hostMaxLength)
        return fail("too_long");
    const literal = parseIpAddress(raw);
    if (literal !== null) {
        if (literal.width === 128)
            return fail("host_ipv6_unsupported");
        if (!isPublicRange(literal, literal.width))
            return fail("address_not_public");
        return isSipDialHost(raw) ? ok(raw) : fail("host_invalid");
    }
    const label = raw.split(".");
    const shaped = label.every((each) => each.length <= SIP_TRUNK_LIMIT.hostLabelMaxLength && LDH_LABEL.test(each));
    // All-numeric labels that are not a valid IPv4 literal (`1.2.3`, `999.1.1.1`) are refused rather
    // than handed to a resolver that may read them as an address.
    if (!shaped || label.every((each) => NUMERIC_LABEL.test(each)))
        return fail("host_invalid");
    // The dial's own host rule, restated so the two can never drift apart (integration ML1).
    return isSipDialHost(raw) ? ok(raw) : fail("host_invalid");
}
/** Visible ASCII without `"` and `\`, which would break the digest's quoted username. */
const PRINTABLE_USERNAME = /^[\x21\x23-\x5b\x5d-\x7e]+$/;
function checkAuthUsername(raw) {
    if (raw === undefined)
        return fail("required");
    if (typeof raw !== "string")
        return fail("wrong_type");
    if (raw.length === 0)
        return fail("empty");
    if (raw.length > SIP_TRUNK_LIMIT.authUsernameMaxLength)
        return fail("too_long");
    return PRINTABLE_USERNAME.test(raw) ? ok(raw) : fail("not_printable");
}
function checkPassword(raw) {
    if (raw === undefined)
        return fail("required");
    if (typeof raw !== "string")
        return fail("wrong_type");
    if (raw.length === 0)
        return fail("empty");
    return lengthOf(raw) <= SIP_TRUNK_LIMIT.passwordMaxLength ? ok(raw) : fail("too_long");
}
function checkNationalCountry(raw) {
    if (raw === undefined || raw === null)
        return ok(null);
    if (typeof raw !== "string")
        return fail("wrong_type");
    if (!isCountryCode(raw))
        return fail("country_invalid");
    return nationalNumberRuleOf(raw) === undefined ? fail("national_rule_missing") : ok(raw);
}
const IPV4_OCTET = "(?:25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9]?[0-9])";
const IPV4 = new RegExp(`^${IPV4_OCTET}(?:\\.${IPV4_OCTET}){3}$`);
const HEX_GROUP = /^[0-9A-Fa-f]{1,4}$/;
function ipv4Value(text) {
    if (!IPV4.test(text))
        return null;
    return text.split(".").reduce((acc, octet) => (acc << 8n) | BigInt(Number(octet)), 0n);
}
function ipv6Value(text) {
    const half = text.split("::");
    if (half.length > 2)
        return null;
    const groupsOf = (part) => (part === "" ? [] : part.split(":"));
    const head = groupsOf(half[0] ?? "");
    const tail = half.length === 2 ? groupsOf(half[1] ?? "") : [];
    if (head === null || tail === null)
        return null;
    // An embedded IPv4 tail (`::ffff:192.0.2.1`) counts as two groups.
    const last = half.length === 2 ? tail : head;
    let embedded = null;
    const lastGroup = last.at(-1);
    if (lastGroup !== undefined && lastGroup.includes(".")) {
        embedded = ipv4Value(lastGroup);
        if (embedded === null)
            return null;
        last.pop();
    }
    const width = head.length + tail.length + (embedded === null ? 0 : 2);
    if (half.length === 2 ? width > 7 : width !== 8)
        return null;
    if (![...head, ...tail].every((group) => HEX_GROUP.test(group)))
        return null;
    const zeroGroup = half.length === 2 ? 8 - width : 0;
    const group = [...head, ...Array(zeroGroup).fill("0"), ...tail];
    let value = group.reduce((acc, each) => (acc << 16n) | BigInt(Number.parseInt(each, 16)), 0n);
    if (embedded !== null)
        value = (value << 32n) | embedded;
    return value;
}
/** A bare IPv4 or IPv6 literal (no brackets, no zone), or `null`. */
export function parseIpAddress(text) {
    const v4 = ipv4Value(text);
    if (v4 !== null)
        return { width: 32, value: v4 };
    if (!text.includes(":"))
        return null;
    const v6 = ipv6Value(text);
    return v6 === null ? null : { width: 128, value: v6 };
}
function block(text, prefix) {
    const parsed = parseIpAddress(text);
    if (parsed === null)
        throw new RangeError(`not an address: ${text}`);
    return { width: parsed.width, network: parsed.value, prefix };
}
/**
 * Not public: private, shared (CGNAT), loopback, link-local, multicast, unspecified and reserved
 * space (RFC 1918, 6598, 1122, 3927, 5771, 1112; RFC 4193, 4291). The documentation ranges (RFC
 * 5737, RFC 3849) are deliberately NOT here: they are what tests and examples use.
 */
const NON_PUBLIC = Object.freeze([
    block("0.0.0.0", 8),
    block("10.0.0.0", 8),
    block("100.64.0.0", 10),
    block("127.0.0.0", 8),
    block("169.254.0.0", 16),
    block("172.16.0.0", 12),
    block("192.168.0.0", 16),
    block("224.0.0.0", 4),
    block("240.0.0.0", 4),
    block("::", 128),
    block("::1", 128),
    block("::ffff:0:0", 96),
    block("fc00::", 7),
    block("fe80::", 10),
    block("ff00::", 8),
]);
function overlaps(a, b) {
    if (a.width !== b.width)
        return false;
    const shift = BigInt(a.width - Math.min(a.prefix, b.prefix));
    return a.network >> shift === b.network >> shift;
}
function isPublicRange(address, prefix) {
    const candidate = { width: address.width, network: address.value, prefix };
    return !NON_PUBLIC.some((each) => overlaps(candidate, each));
}
const PREFIX_LENGTH = /^(?:0|[1-9][0-9]{0,2})$/;
/** One `allowed_address` entry → its canonical key, or the first rule it fails. */
function checkAllowedAddress(raw) {
    if (typeof raw !== "string")
        return fail("wrong_type");
    const slash = raw.indexOf("/");
    const addressText = slash < 0 ? raw : raw.slice(0, slash);
    const address = parseIpAddress(addressText);
    if (address === null)
        return fail("address_invalid");
    let prefix = address.width;
    if (slash >= 0) {
        const prefixText = raw.slice(slash + 1);
        if (!PREFIX_LENGTH.test(prefixText))
            return fail("address_invalid");
        prefix = Number(prefixText);
        if (prefix > address.width)
            return fail("address_invalid");
    }
    const minimum = address.width === 32 ? SIP_TRUNK_LIMIT.ipv4PrefixMin : SIP_TRUNK_LIMIT.ipv6PrefixMin;
    if (prefix < minimum)
        return fail("address_too_broad");
    const hostBit = (1n << BigInt(address.width - prefix)) - 1n;
    if ((address.value & hostBit) !== 0n)
        return fail("address_host_bits_set");
    if (!isPublicRange(address, prefix))
        return fail("address_not_public");
    return ok({ text: raw, key: `${address.width}:${address.value.toString(16)}/${prefix}` });
}
// ── The collector ──────────────────────────────────────────────────────────────────────────
class Collector {
    error = [];
    take(field, checked) {
        if (checked.ok)
            return checked.value;
        this.error.push({ field, code: checked.code });
        return undefined;
    }
    add(field, code) {
        this.error.push({ field, code });
    }
    /** Records a whole-field refusal and yields nothing. */
    refuse(field, code) {
        this.add(field, code);
        return undefined;
    }
    /** Whether `field`, or anything nested in it, has an error. */
    has(field) {
        return this.error.some((each) => each.field === field || each.field.startsWith(`${field}[`) || each.field.startsWith(`${field}.`));
    }
    rejection() {
        return Object.freeze({ kind: "rejected", error: Object.freeze([...this.error]) });
    }
}
function asRecord(input) {
    return typeof input === "object" && input !== null && !Array.isArray(input) ? input : null;
}
/** Refuses every key outside `known`, named `<parent>.<key>` when `parent` is given. */
function refuseUnknown(record, known, out, parent) {
    for (const key of Object.keys(record)) {
        if (!known.includes(key))
            out.add(parent === undefined ? key : `${parent}.${key}`, "unknown_field");
    }
}
function checkAllowedAddressList(raw, out) {
    if (raw === undefined)
        return out.refuse("allowed_address", "required");
    if (!Array.isArray(raw))
        return out.refuse("allowed_address", "wrong_type");
    if (raw.length > SIP_TRUNK_LIMIT.allowedAddressMaxCount)
        return out.refuse("allowed_address", "too_many");
    const seen = new Set();
    const text = [];
    raw.forEach((each, at) => {
        const checked = out.take(`allowed_address[${at}]`, checkAllowedAddress(each));
        if (checked === undefined)
            return;
        if (seen.has(checked.key))
            out.add(`allowed_address[${at}]`, "duplicate");
        seen.add(checked.key);
        text.push(checked.text);
    });
    return out.has("allowed_address") ? undefined : Object.freeze(text);
}
function checkDestinationCountryList(raw, out) {
    const field = "allowed_destination_country";
    if (raw === undefined)
        return out.refuse(field, "required");
    if (!Array.isArray(raw))
        return out.refuse(field, "wrong_type");
    if (raw.length === 0)
        return out.refuse(field, "empty");
    if (raw.length > SIP_TRUNK_LIMIT.allowedDestinationCountryMaxCount)
        return out.refuse(field, "too_many");
    const country = [];
    raw.forEach((each, at) => {
        const at_ = `${field}[${at}]`;
        if (typeof each !== "string")
            return out.add(at_, "wrong_type");
        if (!isCountryCode(each))
            return out.add(at_, "country_invalid");
        if (!isReviewedDestinationCountry(each))
            return out.add(at_, "country_unreviewed");
        if (country.includes(each))
            return out.add(at_, "duplicate");
        country.push(each);
    });
    return out.has(field) ? undefined : Object.freeze(country);
}
const NUMBER_FIELD = ["e164", "is_caller_id"];
/**
 * One `{ e164, is_caller_id }` object. `field` is its name in the body: `number[3]` inside a create
 * body, `undefined` when the object IS the body (`addSipTrunkNumber`).
 */
function checkNumber(raw, field, out) {
    const name = (key) => (field === undefined ? key : `${field}.${key}`);
    const record = asRecord(raw);
    if (record === null)
        return out.refuse(field ?? "body", "wrong_type");
    refuseUnknown(record, NUMBER_FIELD, out, field);
    const e164 = record["e164"];
    const isCallerId = record["is_caller_id"];
    let e164Value;
    if (e164 === undefined)
        out.add(name("e164"), "required");
    else if (typeof e164 !== "string")
        out.add(name("e164"), "wrong_type");
    else if (!isE164(e164))
        out.add(name("e164"), "not_e164");
    else
        e164Value = e164;
    if (isCallerId === undefined)
        out.add(name("is_caller_id"), "required");
    else if (typeof isCallerId !== "boolean")
        out.add(name("is_caller_id"), "wrong_type");
    const failed = field === undefined ? out.error.length > 0 : out.has(field);
    if (e164Value === undefined || typeof isCallerId !== "boolean" || failed)
        return undefined;
    return Object.freeze({ e164: e164Value, isCallerId });
}
function checkNumberList(raw, out) {
    if (raw === undefined)
        return out.refuse("number", "required");
    if (!Array.isArray(raw))
        return out.refuse("number", "wrong_type");
    if (raw.length > SIP_TRUNK_LIMIT.numberMaxCount)
        return out.refuse("number", "too_many");
    const seen = new Set();
    const number = [];
    raw.forEach((each, at) => {
        const checked = checkNumber(each, `number[${at}]`, out);
        if (checked === undefined)
            return;
        if (seen.has(checked.e164))
            return out.add(`number[${at}].e164`, "duplicate");
        seen.add(checked.e164);
        number.push(checked);
    });
    return out.has("number") ? undefined : Object.freeze(number);
}
// ── The settings, shared by create and patch ────────────────────────────────────────────────
const SETTING_FIELD = [
    "display_name",
    "host",
    "port",
    "transport",
    "media_encryption",
    "auth_username",
    "allowed_address",
    "wire_number_format",
    "national_country",
    "caller_id_presentation",
    "allowed_destination_country",
    "codec_policy",
    "inbound_delivery",
];
const CONNECTION_FIELD = ["host", "port", "transport", "auth_username"];
/** Each field on its own; `undefined` for a field that failed (its error is in `out`). */
function checkSettingField(record, out) {
    return {
        displayName: out.take("display_name", checkDisplayName(record["display_name"])),
        host: out.take("host", checkHost(record["host"])),
        port: out.take("port", checkPort(record["port"])),
        transport: out.take("transport", checkOption(record["transport"], SIP_TRANSPORT)),
        mediaEncryption: out.take("media_encryption", checkOption(record["media_encryption"], SIP_MEDIA_ENCRYPTION)),
        authUsername: out.take("auth_username", checkAuthUsername(record["auth_username"])),
        allowedAddress: checkAllowedAddressList(record["allowed_address"], out),
        wireNumberFormat: out.take("wire_number_format", checkOption(record["wire_number_format"], WIRE_NUMBER_FORMAT)),
        nationalCountry: out.take("national_country", checkNationalCountry(record["national_country"])),
        callerIdPresentation: out.take("caller_id_presentation", checkOption(record["caller_id_presentation"], CALLER_ID_PRESENTATION)),
        allowedDestinationCountry: checkDestinationCountryList(record["allowed_destination_country"], out),
        codecPolicy: out.take("codec_policy", checkOption(record["codec_policy"], SIP_CODEC_POLICY)),
        inboundDelivery: out.take("inbound_delivery", checkOption(record["inbound_delivery"], SIP_INBOUND_DELIVERY)),
    };
}
function complete(setting) {
    const { displayName, host, port, transport, mediaEncryption, authUsername, allowedAddress, wireNumberFormat, nationalCountry, callerIdPresentation, allowedDestinationCountry, codecPolicy, inboundDelivery, } = setting;
    if (displayName === undefined ||
        host === undefined ||
        port === undefined ||
        transport === undefined ||
        mediaEncryption === undefined ||
        authUsername === undefined ||
        allowedAddress === undefined ||
        wireNumberFormat === undefined ||
        nationalCountry === undefined ||
        callerIdPresentation === undefined ||
        allowedDestinationCountry === undefined ||
        codecPolicy === undefined ||
        inboundDelivery === undefined) {
        return undefined;
    }
    return Object.freeze({
        displayName,
        host,
        port,
        transport,
        mediaEncryption,
        authUsername,
        allowedAddress,
        wireNumberFormat,
        nationalCountry,
        callerIdPresentation,
        allowedDestinationCountry,
        codecPolicy,
        inboundDelivery,
    });
}
/** The rules between fields, run only on fields that passed their own rules. */
function checkAcrossField(setting, out) {
    if (setting.mediaEncryption === "srtp_required" && setting.transport !== undefined && setting.transport !== "tls") {
        out.add("media_encryption", "srtp_requires_tls");
    }
    const nationalNeeded = setting.wireNumberFormat === "national" || setting.callerIdPresentation === "number_national";
    if (nationalNeeded && setting.nationalCountry === null)
        out.add("national_country", "national_country_required");
    // Integration M1 (2026-09-30): `account_name` puts the auth username in the INVITE's From user, so it
    // must be a user part the dial carries (`SIP_WIRE_FORM_PATTERN`) — refused here, at its field, rather
    // than accepted and then failing every dial after the 201.
    if (setting.callerIdPresentation === "account_name" &&
        setting.authUsername !== undefined &&
        !isSipWireForm(setting.authUsername)) {
        out.add("auth_username", "username_not_a_caller_id");
    }
    // ST-KM (2026-10-02): the registration front registers over UDP only in this release.
    if (setting.inboundDelivery === "registration" && setting.transport !== undefined && setting.transport !== "udp") {
        out.add("inbound_delivery", "registration_needs_udp");
    }
}
/** Every number must be writable in the trunk's wire format, and every caller-ID number in its presentation. */
function checkNumberAgainstSetting(number, setting, out) {
    number.forEach((each, at) => {
        const writable = (format) => {
            try {
                toWireNumber(each.e164, format, setting.nationalCountry);
                return true;
            }
            catch {
                return false;
            }
        };
        const wireOk = writable(setting.wireNumberFormat);
        const presentationOk = !each.isCallerId || setting.callerIdPresentation !== "number_national" || writable("national");
        if (!wireOk || !presentationOk)
            out.add(`number[${at}].e164`, "not_in_national_country");
    });
}
// ── Inbound-only trunks (plan-015 SIP, gap G13, ADR 0046) ─────────────────────────────────────
/** Whether a body names any outbound field with a value (`null` names nothing). */
function namesOutbound(record) {
    return SIP_TRUNK_OUTBOUND_FIELD.some((field) => record[field] !== undefined && record[field] !== null);
}
/**
 * The settings of an inbound-only trunk: every setting but the outbound half, `direct` delivery, and at least one
 * allowed address (the address half of PD8-5). A `registration` trunk needs the outbound half — WeTalk signs in
 * to the provider with it — so asking for one without it is `required` on `host`.
 */
function checkInboundOnlySetting(record, out) {
    const displayName = out.take("display_name", checkDisplayName(record["display_name"]));
    const transport = out.take("transport", checkOption(record["transport"], SIP_TRANSPORT));
    const mediaEncryption = out.take("media_encryption", checkOption(record["media_encryption"], SIP_MEDIA_ENCRYPTION));
    const allowedAddress = checkAllowedAddressList(record["allowed_address"], out);
    if (allowedAddress?.length === 0)
        out.add("allowed_address", "empty");
    const wireNumberFormat = out.take("wire_number_format", checkOption(record["wire_number_format"], WIRE_NUMBER_FORMAT));
    const nationalCountry = out.take("national_country", checkNationalCountry(record["national_country"]));
    const callerIdPresentation = out.take("caller_id_presentation", checkOption(record["caller_id_presentation"], CALLER_ID_PRESENTATION));
    const codecPolicy = out.take("codec_policy", checkOption(record["codec_policy"], SIP_CODEC_POLICY));
    const inboundDelivery = out.take("inbound_delivery", checkOption(record["inbound_delivery"], SIP_INBOUND_DELIVERY));
    if (inboundDelivery === "registration")
        out.add("host", "required");
    if (mediaEncryption === "srtp_required" && transport !== undefined && transport !== "tls") {
        out.add("media_encryption", "srtp_requires_tls");
    }
    if ((wireNumberFormat === "national" || callerIdPresentation === "number_national") && nationalCountry === null) {
        out.add("national_country", "national_country_required");
    }
    if (displayName === undefined ||
        transport === undefined ||
        mediaEncryption === undefined ||
        allowedAddress === undefined ||
        allowedAddress.length === 0 ||
        wireNumberFormat === undefined ||
        nationalCountry === undefined ||
        callerIdPresentation === undefined ||
        codecPolicy === undefined ||
        inboundDelivery !== "direct") {
        return undefined;
    }
    return Object.freeze({
        displayName,
        host: null,
        port: null,
        transport,
        mediaEncryption,
        authUsername: null,
        allowedAddress,
        wireNumberFormat,
        nationalCountry,
        callerIdPresentation,
        allowedDestinationCountry: null,
        codecPolicy,
        inboundDelivery,
    });
}
const CREATE_FIELD = [...SETTING_FIELD, "password", "number"];
/**
 * `SipTrunkCreate` in either shape (plan-015 SIP, ADR 0046) → the validated input, or every field error. A body
 * that names none of the outbound fields (`host`, `port`, `auth_username`, `password`,
 * `allowed_destination_country`) is an INBOUND-ONLY trunk: `direct`, at least one allowed address, no password —
 * WeTalk generates the inbound pair. A body that names any of them is a two-way trunk and needs them all
 * (`validateSipTrunkInput`, each one missing `required`).
 */
export function validateSipTrunkCreate(input) {
    const record = asRecord(input);
    if (record === null || namesOutbound(record))
        return validateSipTrunkInput(input);
    const out = new Collector();
    refuseUnknown(record, CREATE_FIELD, out);
    const setting = checkInboundOnlySetting(Object.hasOwn(record, "inbound_delivery") ? record : { ...record, inbound_delivery: "direct" }, out);
    const number = checkNumberList(record["number"], out);
    if (setting !== undefined && number !== undefined)
        checkNumberAgainstSetting(number, setting, out);
    if (out.error.length > 0 || setting === undefined || number === undefined)
        return out.rejection();
    const inboundOnly = Object.freeze({ ...setting, password: null, number });
    return inboundOnly;
}
/** `SipTrunkCreate` → the validated input, or every field error. */
export function validateSipTrunkInput(input) {
    const out = new Collector();
    const record = asRecord(input);
    if (record === null) {
        out.add("body", "wrong_type");
        return out.rejection();
    }
    refuseUnknown(record, CREATE_FIELD, out);
    // ST-KM: `inbound_delivery` may be left out of a create; absent is `direct`, what every trunk was before it.
    const partial = checkSettingField(Object.hasOwn(record, "inbound_delivery") ? record : { ...record, inbound_delivery: "direct" }, out);
    const password = out.take("password", checkPassword(record["password"]));
    const number = checkNumberList(record["number"], out);
    checkAcrossField(partial, out);
    const setting = complete(partial);
    if (setting !== undefined && number !== undefined)
        checkNumberAgainstSetting(number, setting, out);
    if (out.error.length > 0 || setting === undefined || password === undefined || number === undefined) {
        return out.rejection();
    }
    return Object.freeze({ ...setting, password, number });
}
/**
 * `updateSipTrunk`'s body — any create field except `password` and `number` — merged over the
 * trunk's current settings and validated as a whole, so a patch cannot break a rule between fields
 * (`transport: "udp"` on an `srtp_required` trunk is refused). `numberOnTrunk` are the trunk's
 * numbers, re-checked against a changed wire format or presentation.
 */
export function validateSipTrunkPatch(input, current, numberOnTrunk) {
    const out = new Collector();
    const record = asRecord(input);
    if (record === null) {
        out.add("body", "wrong_type");
        return out.rejection();
    }
    if (current.host === null)
        return validateInboundOnlyPatch(record, current, numberOnTrunk, out);
    refuseUnknown(record, SETTING_FIELD, out);
    const merged = {
        display_name: current.displayName,
        host: current.host,
        port: current.port,
        transport: current.transport,
        media_encryption: current.mediaEncryption,
        auth_username: current.authUsername,
        allowed_address: current.allowedAddress,
        wire_number_format: current.wireNumberFormat,
        national_country: current.nationalCountry,
        caller_id_presentation: current.callerIdPresentation,
        allowed_destination_country: current.allowedDestinationCountry,
        codec_policy: current.codecPolicy,
        inbound_delivery: current.inboundDelivery,
    };
    for (const field of SETTING_FIELD)
        if (Object.hasOwn(record, field))
            merged[field] = record[field];
    const partial = checkSettingField(merged, out);
    checkAcrossField(partial, out);
    const setting = complete(partial);
    if (setting !== undefined)
        checkNumberAgainstSetting(numberOnTrunk, setting, out);
    if (out.error.length > 0 || setting === undefined)
        return out.rejection();
    const changedField = SETTING_FIELD.filter((field) => Object.hasOwn(record, field) && !sameValue(currentValue(current, field), record[field]));
    return Object.freeze({
        kind: "patch",
        setting,
        changedField: Object.freeze(changedField),
        connectionEdited: changedField.some((field) => CONNECTION_FIELD.includes(field)),
        outboundAdded: false,
        password: null,
    });
}
const INBOUND_ONLY_PATCH_FIELD = [...SETTING_FIELD, "password"];
/**
 * plan-015 SIP (ADR 0046): a patch of an INBOUND-ONLY trunk. Without outbound fields it stays inbound-only (its
 * settings validated as such: `direct`, an allowed address left). With any of them it becomes two-way — the
 * whole outbound half arrives together, the password included (each one missing `required`), validated as a
 * two-way trunk over the merged settings; a connection edit, so the trunk is `unverified` until its check.
 */
function validateInboundOnlyPatch(record, current, numberOnTrunk, out) {
    refuseUnknown(record, INBOUND_ONLY_PATCH_FIELD, out);
    const merged = {
        display_name: current.displayName,
        transport: current.transport,
        media_encryption: current.mediaEncryption,
        allowed_address: current.allowedAddress,
        wire_number_format: current.wireNumberFormat,
        national_country: current.nationalCountry,
        caller_id_presentation: current.callerIdPresentation,
        codec_policy: current.codecPolicy,
        inbound_delivery: current.inboundDelivery,
    };
    const outboundAdded = namesOutbound(record);
    for (const field of SETTING_FIELD) {
        if (!Object.hasOwn(record, field))
            continue;
        // Naming an outbound field `null` on a trunk without that half changes nothing.
        if (!outboundAdded && SIP_TRUNK_OUTBOUND_FIELD.includes(field) && record[field] === null)
            continue;
        merged[field] = record[field];
    }
    const changedField = SETTING_FIELD.filter((field) => Object.hasOwn(record, field) && record[field] !== null && !sameValue(currentValue(current, field), record[field]));
    if (!outboundAdded) {
        const setting = checkInboundOnlySetting(merged, out);
        if (setting !== undefined)
            checkNumberAgainstSetting(numberOnTrunk, setting, out);
        if (out.error.length > 0 || setting === undefined)
            return out.rejection();
        return Object.freeze({
            kind: "patch",
            setting,
            changedField: Object.freeze(changedField),
            connectionEdited: false,
            outboundAdded: false,
            password: null,
        });
    }
    const partial = checkSettingField(merged, out);
    const password = out.take("password", checkPassword(record["password"]));
    checkAcrossField(partial, out);
    const setting = complete(partial);
    if (setting !== undefined)
        checkNumberAgainstSetting(numberOnTrunk, setting, out);
    if (out.error.length > 0 || setting === undefined || password === undefined)
        return out.rejection();
    return Object.freeze({
        kind: "patch",
        setting,
        changedField: Object.freeze([...changedField, "password"]),
        connectionEdited: true,
        outboundAdded: true,
        password,
    });
}
function currentValue(current, field) {
    switch (field) {
        case "display_name":
            return current.displayName;
        case "host":
            return current.host;
        case "port":
            return current.port;
        case "transport":
            return current.transport;
        case "media_encryption":
            return current.mediaEncryption;
        case "auth_username":
            return current.authUsername;
        case "allowed_address":
            return current.allowedAddress;
        case "wire_number_format":
            return current.wireNumberFormat;
        case "national_country":
            return current.nationalCountry;
        case "caller_id_presentation":
            return current.callerIdPresentation;
        case "allowed_destination_country":
            return current.allowedDestinationCountry;
        case "codec_policy":
            return current.codecPolicy;
        case "inbound_delivery":
            return current.inboundDelivery;
    }
}
function sameValue(a, b) {
    if (Array.isArray(a) && Array.isArray(b))
        return a.length === b.length && a.every((each, at) => each === b[at]);
    return a === b;
}
/** `addSipTrunkNumber`'s body `{ e164, is_caller_id }`, checked against the trunk's settings. */
export function validateSipTrunkNumberInput(input, setting) {
    const out = new Collector();
    // Checked as the body itself: its fields are `e164` and `is_caller_id`, not `number[0].…`.
    const number = checkNumber(input, undefined, out);
    if (number === undefined)
        return out.rejection();
    const scratch = new Collector();
    checkNumberAgainstSetting([number], setting, scratch);
    if (scratch.error.length > 0) {
        out.add("e164", "not_in_national_country");
        return out.rejection();
    }
    return number;
}
/** `replaceSipTrunkPassword`'s body `{ password }`. The value is returned, never echoed in an error. */
export function validateSipTrunkPassword(input) {
    const out = new Collector();
    const record = asRecord(input);
    if (record === null) {
        out.add("body", "wrong_type");
        return out.rejection();
    }
    refuseUnknown(record, ["password"], out);
    const password = out.take("password", checkPassword(record["password"]));
    if (out.error.length > 0 || password === undefined)
        return out.rejection();
    return Object.freeze({ password });
}
//# sourceMappingURL=validate.js.map